import { Barcode, StoreProduct, StoreProductFraction } from '@bofrak-backend/shared';

export type ProductLike = StoreProduct | StoreProductFraction;

/** Build a display name like "Rice (1kg)" or "Rice (Box=6)" */
export function productDisplayName(p: ProductLike): string {
  const unit = String(p.unit ?? '').replace(/[()]/g, '').trim();
  if (unit) return `${p.name} (${unit})`;
  const qty = p.quantity_in_unit_package ?? 0;
  return `${p.name} (Box=${Math.floor(qty)})`;
}

/** Get formatted price text for a product (single or range). */
export function priceText(
  p: ProductLike,
  format: (v: number) => string,
): string {
  if ('is_sold_by_range' in p && p.is_sold_by_range) {
    return `${format(p.min_selling_price)} – ${format(p.max_selling_price)}`;
  }
  return format(p.price);
}

/** Get price range text if applicable, else undefined. */
export function priceRangeText(
  p: ProductLike,
  format: (v: number) => string,
): string | undefined {
  if ('is_sold_by_range' in p && p.is_sold_by_range) {
    return `${format(p.min_selling_price)} – ${format(p.max_selling_price)}`;
  }
  return undefined;
}

/** Build a PDF filename like "qrcode-Rice-1kg-123456.pdf" */
export function labelFileName(
  mode: 'qrcode' | 'barcode' | 'plu',
  name: string,
  code?: string,
): string {
  const clean = name.replace(/[()]/g, '').replace(/\s+/g, '-').trim();
  const parts = [mode, clean];
  if (code) parts.push(code);
  return `${parts.join('-')}.pdf`;
}

/** Build a PDF filename from a ProductLike */
export function labelFileNameFromProduct(
  mode: 'qrcode' | 'barcode' | 'plu',
  p: ProductLike,
  code?: string,
): string {
  return labelFileName(mode, productDisplayName(p), code);
}

/** Build BarcodeLabelData[] for all barcodes of a given product. */
export function buildLabelData(
  p: ProductLike,
  format: (v: number) => string,
) {
  const barcodes: Barcode[] =
    (p as StoreProduct | StoreProductFraction & { barcodes?: Barcode[] })
      .barcodes ?? [];
  const name = productDisplayName(p);
  const range = priceRangeText(p, format);
  const single = priceText(p, format);

  return barcodes.filter(lb=>lb.type==="default").map((bc) => ({
    value: String(bc.value),
    productName: name,
    price: single,
    priceRange: range,
  }));
}
